export const EXECUTIVE_PORTFOLIO_PROPERTY_COUNT = 240

const SEVERITY_ORDER = ['high', 'medium', 'low']

const SEVERITY_LABELS = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export function getSeverityLabel(severity) {
  return SEVERITY_LABELS[severity] || severity
}

export function buildPropertyAttentionSummary(propertyCount, underperforming = []) {
  const normalizedCount = Math.max(1, Math.round(propertyCount))
  const needsAttentionCount = underperforming.length

  const severityDistribution = underperforming.reduce(
    (acc, item) => {
      if (item.severity && acc[item.severity] != null) {
        acc[item.severity] += 1
      }
      return acc
    },
    { high: 0, medium: 0, low: 0 }
  )

  return {
    propertyCount: normalizedCount,
    needsAttentionCount,
    severityDistribution,
  }
}

export function assignUnderperformerSeverity(items = []) {
  const severityPattern = ['high', 'high', 'medium', 'low']

  return items.map((item, index) => ({
    ...item,
    severity: item.severity || severityPattern[index % severityPattern.length],
  }))
}

const ENGAGEMENT_METRIC_LABEL = 'Engagement rate'

const formatEngagementComparison = (rate, benchmark) =>
  `${Number(rate).toFixed(1)}% vs ${Number(benchmark).toFixed(1)}%`

const formatEngagementVariance = (rate, benchmark) => {
  const diff = Number((rate - benchmark).toFixed(1))
  const prefix = diff > 0 ? '+' : diff < 0 ? '' : ''
  return `${prefix}${diff} pts`
}

const computeBrandEngagementAverages = (properties = []) => {
  const totalsByBrand = {}

  properties.forEach((row) => {
    const brand = row.brand || 'Portfolio'
    if (!totalsByBrand[brand]) {
      totalsByBrand[brand] = { total: 0, count: 0 }
    }
    totalsByBrand[brand].total += row.engagementRate
    totalsByBrand[brand].count += 1
  })

  return Object.fromEntries(
    Object.entries(totalsByBrand).map(([brand, { total, count }]) => [
      brand,
      count ? total / count : 0,
    ])
  )
}

export function buildPropertyOutliersFromEngagement(properties = [], options = {}) {
  const { propertyCount = properties.length } = options

  if (!properties.length) {
    return {
      propertyCount,
      underperforming: [],
      outperforming: [],
      attention: buildPropertyAttentionSummary(propertyCount, []),
    }
  }

  const brandAverages = computeBrandEngagementAverages(properties)

  const scored = properties.map((row) => {
    const benchmark = brandAverages[row.brand] ?? row.engagementRate
    const varianceValue = Number((row.engagementRate - benchmark).toFixed(1))

    return {
      id: row.id,
      propertyId: row.id,
      name: row.property,
      brand: row.brand,
      metric: ENGAGEMENT_METRIC_LABEL,
      engagementRate: row.engagementRate,
      demandIndex: row.demandIndex,
      movement: row.movement,
      benchmark,
      varianceValue,
      variance: formatEngagementVariance(row.engagementRate, benchmark),
      varianceTone: varianceValue >= 0 ? 'positive' : 'negative',
      comparison: formatEngagementComparison(row.engagementRate, benchmark),
    }
  })

  let underperforming = scored
    .filter((item) => item.movement === 'declined')
    .sort((left, right) => left.varianceValue - right.varianceValue)

  if (underperforming.length < 2) {
    underperforming = scored
      .filter((item) => item.varianceValue < 0)
      .sort((left, right) => left.varianceValue - right.varianceValue)
  }

  let outperforming = scored
    .filter((item) => item.movement === 'improved')
    .sort((left, right) => right.varianceValue - left.varianceValue)

  if (outperforming.length < 2) {
    outperforming = scored
      .filter((item) => item.varianceValue > 0)
      .sort((left, right) => right.varianceValue - left.varianceValue)
  }

  const underperformingWithSeverity = assignUnderperformerSeverity(underperforming)

  return {
    propertyCount,
    underperforming: underperformingWithSeverity,
    outperforming,
    attention: buildPropertyAttentionSummary(propertyCount, underperformingWithSeverity),
  }
}

export function getPropertyDashboardSection() {
  return 'social'
}

export function buildPropertyPulseKpis(property) {
  const propertyKey = property?.propertyId || property?.id || property?.name || ''
  let hash = 0
  for (let index = 0; index < propertyKey.length; index += 1) {
    hash = (hash + propertyKey.charCodeAt(index) * (index + 1)) % 997
  }

  const engagementRate = Number(property?.engagementRate) || 4
  const scale = Math.max(0.35, engagementRate / 4.7)
  const formatCount = (value) => value.toLocaleString('en-US')

  const messages = Math.round(180 + (hash % 280) * scale)
  const messagesDelta = Math.round(8 + (hash % 40) * scale)
  const engagements = Math.round(320 + (hash % 520) * scale)
  const engagementDeltaPct = Number((2 + (hash % 18) * scale * 0.1).toFixed(1))
  const publishedPosts = Math.round(12 + (hash % 45) * scale)
  const postsDelta = Math.round(3 + (hash % 20) * scale)
  const followers = Math.round(28000 + (hash % 120000) * scale)
  const followerDelta = Math.round(-15 + (hash % 50) * scale - 20)
  const netGrowth = Math.round(20 + (hash % 80) * scale)

  return [
    {
      id: 'inbox',
      label: 'Inbox Health',
      value: formatCount(messages),
      delta: formatCount(messagesDelta),
      deltaDirection: messagesDelta >= 0 ? 'up' : 'down',
      subLabel: 'Inbound messages',
      calculation: 'Inbound messages across all connected channels.',
      dataSource: 'SoHo inbox · connected channels',
    },
    {
      id: 'engagement',
      label: 'Engagement',
      value: formatCount(engagements),
      delta: `${engagementDeltaPct}%`,
      deltaDirection: engagementDeltaPct >= 0 ? 'up' : 'down',
      subLabel: 'Total actions',
      calculation: 'Likes, comments, shares, and saves across platforms.',
      dataSource: 'Instagram · Facebook · TikTok · Google (via SoHo)',
    },
    {
      id: 'published-posts',
      label: 'Published Posts',
      value: formatCount(publishedPosts),
      delta: formatCount(postsDelta),
      deltaDirection: postsDelta >= 0 ? 'up' : 'down',
      subLabel: 'Published',
      calculation: 'Posts currently live across your connected platforms.',
      dataSource: 'SoHo publishing',
    },
    {
      id: 'total-followers',
      label: 'Total Followers',
      value: formatCount(followers),
      delta: formatCount(Math.abs(followerDelta)),
      deltaDirection: followerDelta >= 0 ? 'up' : 'down',
      context: `Net growth +${formatCount(netGrowth)}`,
      contextTone: 'positive',
      calculation: 'Total followers and net growth across connected platforms.',
      dataSource: 'Instagram · Facebook · TikTok · Google (via SoHo)',
    },
  ]
}

export function buildPropertyDashboardDeepLink(property) {
  return {
    propertyId: property.propertyId || property.id,
    propertyName: property.name,
    section: getPropertyDashboardSection(property),
    platform: 'all',
  }
}

export function formatSeverityDistribution(distribution) {
  return SEVERITY_ORDER.filter((key) => distribution?.[key] > 0).map((key) => ({
    key,
    label: SEVERITY_LABELS[key],
    count: distribution[key],
  }))
}

const countByCategory = (items = []) =>
  items.reduce((acc, item) => {
    const category = item.brand || 'Portfolio'
    acc[category] = (acc[category] || 0) + 1
    return acc
  }, {})

const topCategoryEntry = (items = []) =>
  Object.entries(countByCategory(items)).sort((left, right) => right[1] - left[1])[0]

export function buildAttentionInsight(items = []) {
  if (!items.length) {
    return 'No properties are trailing their category engagement average.'
  }

  const highItems = items.filter((item) => item.severity === 'high')
  const highCategory = topCategoryEntry(highItems)
  if (highCategory && highItems.length >= 2 && highCategory[1] / highItems.length >= 0.4) {
    return `${highCategory[0]} accounts for ${highCategory[1]} of ${highItems.length} high-severity gaps.`
  }

  const leadingCategory = topCategoryEntry(items)
  if (leadingCategory && leadingCategory[1] > 1 && leadingCategory[1] / items.length >= 0.35) {
    return `${leadingCategory[0]} has the most properties below category average (${leadingCategory[1]}).`
  }

  const worst = [...items].sort((left, right) => left.varianceValue - right.varianceValue)[0]
  if (worst?.name && worst.variance) {
    return `Largest gap is ${worst.name} at ${worst.variance}.`
  }

  return `${items.length} properties are below their category engagement average.`
}
